import { useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Slider } from '@/components/ui-kit/slider';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { cn } from '@/lib/utils';
import { useGetKpiConfiguration, useSaveKpiConfiguration } from '../../hooks/use-kpi-configuration';
import {
  isCloseToHundred,
  kpiConfigurationFormSchema,
  KpiConfigurationFormValues,
} from '../../schemas/kpi-configuration-form-schema';
import { toKpiConfigurationFormValues } from '../../utils/kpi-configuration';

const SLIDER_MIN = 0;
const SLIDER_MAX = 200;

const CARD_CLASS = 'rounded-xl border bg-card p-4 shadow-sm sm:p-6';

const ValuePill = ({ label, value }: { label: string; value: number }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
    {label}
    <span className="text-sm font-semibold tabular-nums">{value}</span>
  </span>
);

const WeightGroupFields = ({
  title,
  weights,
  onChange,
}: {
  title: string;
  weights: Record<string, number>;
  onChange: (weights: Record<string, number>) => void;
}) => {
  const { t } = useTranslation();
  const idPrefix = useId();
  const total = Object.values(weights).reduce((sum, value) => sum + (value || 0), 0);
  const roundedTotal = Number(total.toFixed(2));
  const isValid = isCloseToHundred(weights);

  return (
    <section className={cn(CARD_CLASS, 'flex h-full flex-col gap-4')}>
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold">{title}</h3>
        <span
          className={cn(
            'rounded-full px-2.5 py-1 text-xs font-medium tabular-nums',
            isValid
              ? 'bg-success-subtle text-success-subtle-foreground'
              : 'bg-destructive-subtle text-destructive-subtle-foreground'
          )}
        >
          {t('TOTAL')}: {roundedTotal}%
        </span>
      </header>

      <ul className="divide-y">
        {Object.entries(weights).map(([key, value], index) => {
          const inputId = `${idPrefix}-${index}`;
          return (
            <li key={key} className="flex items-center justify-between gap-3 py-2.5 first:pt-0">
              <label htmlFor={inputId} className="min-w-0 flex-1 text-sm">
                {key}
              </label>
              <div className="relative w-28 shrink-0">
                <Input
                  id={inputId}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={100}
                  className="h-10 bg-background pr-8 text-right tabular-nums"
                  value={value}
                  onChange={(e) => onChange({ ...weights, [key]: e.target.valueAsNumber || 0 })}
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                  %
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-auto space-y-2">
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={roundedTotal}
          className="h-1.5 overflow-hidden rounded-full bg-border"
        >
          <div
            className={cn(
              'h-full rounded-full transition-all',
              isValid ? 'bg-success' : 'bg-destructive'
            )}
            style={{ width: `${Math.min(Math.max(total, 0), 100)}%` }}
          />
        </div>
        {!isValid && <p className="text-xs text-destructive">{t('KPI_CONFIG_ERR_MUST_TOTAL_100')}</p>}
      </div>
    </section>
  );
};

export const KpiConfigurationForm = () => {
  const { t } = useTranslation();
  const { data: configuration, isLoading, isError } = useGetKpiConfiguration();
  const { mutate: saveConfiguration, isPending } = useSaveKpiConfiguration();

  const form = useForm<KpiConfigurationFormValues>({
    mode: 'onChange',
    resolver: async (data, context, options) => {
      const result = await zodResolver(kpiConfigurationFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: toKpiConfigurationFormValues(null),
  });

  // Keyed on the record id, not the record: a background refetch of the same row must not discard
  // edits in progress, while a freshly saved row (new id) should replace what is shown.
  const configurationId = configuration?.ItemId;
  useEffect(() => {
    if (isLoading) return;
    form.reset(toKpiConfigurationFormValues(configuration));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, configurationId, form]);

  const minLevel = form.watch('QnMatrixMinLevel');
  const { isDirty, isValid } = form.formState;

  const handleSubmit = (values: KpiConfigurationFormValues) => saveConfiguration(values);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">{t('LOADING')}</p>;
  }

  if (isError) {
    return <p className="text-sm text-destructive">{t('SOMETHING_WENT_WRONG')}</p>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="w-full space-y-6">
        <div className="space-y-1">
          <h2 className="text-base font-semibold">{t('KPI_CONFIG_TITLE')}</h2>
          <p className="text-sm text-muted-foreground">{t('KPI_CONFIG_SUBTITLE')}</p>
        </div>

        <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
          <section className={cn(CARD_CLASS, 'lg:col-span-2')}>
            <div className="grid gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] md:gap-10">
              <FormField
                control={form.control}
                name="QnMatrixMaxLevel"
                render={({ field }) => (
                  <FormItem className="min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <FormLabel>{t('QUANTITATIVE_MATRIX_RANGE')}</FormLabel>
                      <div className="flex items-center gap-2">
                        <ValuePill label={t('MIN')} value={minLevel} />
                        <ValuePill label={t('MAX')} value={field.value} />
                      </div>
                    </div>
                    <FormControl>
                      <Slider
                        className="py-3"
                        min={SLIDER_MIN}
                        max={SLIDER_MAX}
                        step={1}
                        value={[minLevel, field.value]}
                        onValueChange={([min, max]) => {
                          form.setValue('QnMatrixMinLevel', min, { shouldDirty: true });
                          field.onChange(max);
                        }}
                      />
                    </FormControl>
                    <div className="flex justify-between text-xs tabular-nums text-muted-foreground">
                      <span>{SLIDER_MIN}</span>
                      <span>{SLIDER_MAX}</span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="NormalizedMaxScale"
                render={({ field }) => (
                  <FormItem className="min-w-0">
                    <FormLabel>{t('NORMALIZED_MAX_SCALE')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="decimal"
                        min={1}
                        className="bg-background"
                        {...field}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </section>

          <FormField
            control={form.control}
            name="ReviewerGroupWeights"
            render={({ field }) => (
              <WeightGroupFields
                title={t('REVIEWER_GROUP_WEIGHTS')}
                weights={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <FormField
            control={form.control}
            name="CategoryWeights"
            render={({ field }) => (
              <WeightGroupFields
                title={t('CATEGORY_WEIGHTS')}
                weights={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        <div className="flex justify-end border-t pt-4">
          <Button
            type="submit"
            className="w-full sm:w-auto sm:min-w-32"
            loading={isPending}
            disabled={isPending || !isDirty || !isValid}
          >
            {t('SAVE')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
